import {
	createContext,
	Dispatch,
	FC,
	ReactNode,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
  import { useAudioRecording, RecordingStatus } from './AudioRecordingContext';
  
  export interface IApiReloadContextProps {
	// Patient data management
	reloadPatient: boolean;
	setReloadPatient: Dispatch<SetStateAction<boolean>>;
	patientID: PatientInfo | null;
	setPatientID: Dispatch<SetStateAction<PatientInfo | null>>;
	
	// Case management
	caseInfo: CaseInfo | null;
	setcaseInfo: Dispatch<SetStateAction<CaseInfo | null>>;
	
	// UI state
	isOnNewNoteScreen: boolean;
	setIsOnNewNoteScreen: Dispatch<SetStateAction<boolean>>;
	
	// Audio state refs (derived from AudioRecordingContext)
	isRecording: boolean;  // Read-only derived from AudioRecordingContext
	currentAudioId: string | null; // Combined patientId-Soapid to identify current audio

	// Notes reload state
	reloadNotes: boolean;
	setReloadNotes: Dispatch<SetStateAction<boolean>>;
  }
  
  // Create the ApiReloadContext
  const ApiReloadContext = createContext<IApiReloadContextProps>({} as IApiReloadContextProps);
  
  // Define the provider component interface
  interface IApiReloadProviderProps {
	children: ReactNode;
  }
  
  export interface PatientInfo {
	firstName?: string;
	lastName?: string;
	email?: string;
	id?: string;
	moblieNo?: string;
  }
  
  export interface CaseInfo {
	id?: string;
	caseName?: string;
	createdAt?: string;
  }
  
  export const ApiReloadProvider: FC<IApiReloadProviderProps> = ({ children }) => {
	// Existing state
	const [reloadPatient, setReloadPatient] = useState(false);
	const [patientID, setPatientID] = useState<PatientInfo | null>(null);
	const [caseInfo, setcaseInfo] = useState<CaseInfo | null>(null);
	const [isOnNewNoteScreen, setIsOnNewNoteScreen] = useState<boolean>(false);
	
	// New state for reloading notes
	const [reloadNotes, setReloadNotes] = useState<boolean>(false);
	
	// Get recording state from the updated audio recording context
	const { recordings, activeRecordingId } = useAudioRecording();

	// Avoid high-frequency context updates from recording timer ticks
	const [isRecording, setIsRecording] = useState(false);
	const [currentAudioId, setCurrentAudioId] = useState<string | null>(null);
	const activeStatus = activeRecordingId
		? recordings[activeRecordingId]?.status
		: RecordingStatus.IDLE;

	useEffect(() => {
		setCurrentAudioId(activeRecordingId);
	}, [activeRecordingId]);

	useEffect(() => {
		setIsRecording(activeStatus === RecordingStatus.RECORDING);
	}, [activeRecordingId, activeStatus]);
  
	// Create memoized context value
	const value: IApiReloadContextProps = useMemo(
	  () => ({
		reloadPatient,
		setReloadPatient,
		patientID,
		setPatientID,
		caseInfo,
		setcaseInfo,
		isOnNewNoteScreen,
		setIsOnNewNoteScreen,
		isRecording,
		currentAudioId,
		reloadNotes,
		setReloadNotes
	  }),
	  [
		reloadPatient, 
		patientID, 
		caseInfo, 
		isOnNewNoteScreen, 
		isRecording,
		currentAudioId,
		reloadNotes
	  ],
	);
  
	return <ApiReloadContext.Provider value={value}>{children}</ApiReloadContext.Provider>;
  };
  
  // Custom hook to use the ApiReloadContext
  export const useApiReload = () => {
	return useContext(ApiReloadContext);
  };